import React from "react";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import { Fieldset } from "primereact/fieldset";
import { Tag } from "primereact/tag";
import { formatDate } from "../helpers";
import "./AppUserLookupModal.css";

const Field = ({ label, value }) => (
  <div className="mb-3">
    <div className="text-color-secondary text-sm mb-1">{label}</div>
    <div className="font-medium">{value ?? "—"}</div>
  </div>
);

const BoolField = ({ label, value }) => (
  <Field label={label} value={value === undefined || value === null ? null : value ? "Yes" : "No"} />
);

const sortPermissions = (permissions) =>
  [...permissions].sort((a, b) => {
    const rank = (name) => {
      if (name === "admin") return 0;
      if (name?.includes("owner")) return 1;
      if (name?.includes("manager")) return 2;
      return 3;
    };
    const diff = rank(a.permission_name) - rank(b.permission_name);
    return diff !== 0 ? diff : a.permission_name.localeCompare(b.permission_name);
  });

const permissionSeverity = (name) => {
  if (name === "admin") return "danger";
  if (name?.includes("owner")) return "warning";
  return "info";
};

const UserPanel = ({ user }) => (
  <div className="flex flex-column gap-4">
    <Fieldset legend="Profile">
      <div className="grid">
        <div className="col-12 md:col-6"><Field label="Name" value={user.name} /></div>
        <div className="col-12 md:col-6"><Field label="Email" value={user.email} /></div>
        <div className="col-12 md:col-6"><Field label="Emaid" value={user.emaid} /></div>
        <div className="col-12 md:col-6"><Field label="Nickname" value={user.nickname} /></div>
        <div className="col-12 md:col-6"><Field label="Given Name" value={user.given_name} /></div>
        <div className="col-12 md:col-6"><Field label="Family Name" value={user.family_name} /></div>
        <div className="col-12 md:col-6"><Field label="IDP Tenant Domain" value={user.idp_tenant_domain} /></div>
      </div>
    </Fieldset>

    <Fieldset legend="Permissions">
      {user.permissions?.length ? (
        <div className="flex flex-wrap gap-2">
          {sortPermissions(user.permissions).map((p) => (
            <Tag
              key={p.permission_name}
              value={p.permission_name}
              severity={permissionSeverity(p.permission_name)}
              className={p.permission_name?.includes("manager") ? "permission-manager" : undefined}
            />
          ))}
        </div>
      ) : (
        <span className="text-color-secondary">No permissions assigned.</span>
      )}
    </Fieldset>

    {user.identities?.length > 0 && (
      <Fieldset legend="Identities">
        <div className="flex flex-column gap-3">
          {user.identities.map((identity, index) => (
            <div
              key={index}
              className="grid p-3"
              style={{ border: "1px solid var(--surface-border)", borderRadius: "var(--border-radius)" }}
            >
              {Object.entries(identity).map(([key, val]) => (
                <div key={key} className="col-12 md:col-6">
                  <Field
                    label={key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                    value={val?.toString()}
                  />
                </div>
              ))}
            </div>
          ))}
        </div>
      </Fieldset>
    )}

    <Fieldset legend="Account Status">
      <div className="grid">
        <div className="col-12 md:col-6"><BoolField label="Email Verified" value={user.email_verified} /></div>
        <div className="col-12 md:col-6"><BoolField label="Blocked" value={user.blocked} /></div>
        <div className="col-12 md:col-6"><BoolField label="Terms Accepted" value={user.terms_accepted} /></div>
        <div className="col-12 md:col-6"><Field label="Terms Accepted Date" value={formatDate(user.terms_accepted_date)} /></div>
        <div className="col-12 md:col-6"><Field label="Phone Number" value={user.phone_number} /></div>
        <div className="col-12 md:col-6"><BoolField label="Phone Verified" value={user.phone_verified} /></div>
        <div className="col-12 md:col-6"><Field label="Multifactor" value={user.multifactor?.join(", ") || null} /></div>
      </div>
    </Fieldset>

    <Fieldset legend="Activity">
      <div className="grid">
        <div className="col-12 md:col-6"><Field label="Created At" value={formatDate(user.created_at)} /></div>
        <div className="col-12 md:col-6"><Field label="Updated At" value={formatDate(user.updated_at)} /></div>
        <div className="col-12 md:col-6"><Field label="Last Login" value={formatDate(user.last_login)} /></div>
        <div className="col-12 md:col-6"><Field label="Last IP" value={user.last_ip} /></div>
        <div className="col-12 md:col-6"><Field label="Logins Count" value={user.logins_count} /></div>
      </div>
    </Fieldset>
  </div>
);

const AppUserLookupModal = ({ users, onHide }) => {
  return (
    <Dialog
      visible={users.length > 0}
      onHide={onHide}
      header="User Lookup"
      style={{ width: "50rem" }}
      breakpoints={{ "960px": "75vw", "641px": "100vw" }}
      className="app-user-lookup-modal"
      maximizable
    >
      {users.length === 1 ? (
        <UserPanel user={users[0]} />
      ) : (
        <TabView>
          {users.map((user, index) => (
            <TabPanel key={user.emaid || index} header={user.emaid || `User ${index + 1}`}>
              <UserPanel user={user} />
            </TabPanel>
          ))}
        </TabView>
      )}
    </Dialog>
  );
};

export default AppUserLookupModal;
